'use client';

import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { productAsset } from '@/lib/asset-urls';
import { RESPONSIVE_WIDTHS } from '@/lib/image-widths.mjs';
import {
    buildResponsiveSrcSet,
    getSizesForLayout,
    isCdnUrl,
} from '@/lib/responsive-image';

/**
 * MultiFormatImage Component
 *
 * Serves WebP to modern browsers with JPEG/PNG fallback via <picture>.
 * Saves 25-35% bandwidth compared to JPEG alone.
 * SVG files are served directly without format conversion.
 *
 * Responsive srcSet support:
 *   CDN extensionless paths generate pre-scaled variant srcSets
 *   (e.g. trench-shield-400w.webp, trench-shield-800w.webp, ...)
 *   so mobile users download appropriately-sized images instead of
 *   the full 1920×1440 original.
 *
 * Usage:
 *   import { productAsset } from '@/lib/asset-urls';
 *
 *   // Extensionless path — component appends .webp / .jpg automatically,
 *   // and generates responsive srcSet for CDN-hosted images.
 *   <MultiFormatImage
 *     src={productAsset('product/trench-shield/trench-shield')}
 *     alt="Trench Shield"
 *     width={600}
 *     height={450}
 *   />
 *
 *   // SVG — served directly (no format conversion)
 *   <MultiFormatImage
 *     src={productAsset('logo/trenchmate-logo.svg')}
 *     alt="Trenchmate Logo"
 *     width={160}
 *     height={40}
 *   />
 *
 * Props:
 *   src       — Asset path WITHOUT extension, or WITH .svg extension.
 *               Use productAsset() / staticAsset() from @/lib/asset-urls to resolve CDN URLs.
 *   alt       — Required alt text
 *   width     — Image width (required if not using fill)
 *   height    — Image height (required if not using fill)
 *   fill      — Boolean, fills containing element (use with position:relative parent)
 *   sizes     — Responsive sizes string (overrides auto-derivation from layout)
 *   layout    — Layout preset for auto-sizing: 'hero', 'card', 'gallery', 'cover', 'full-width'
 *               (default: 'full-width'). Used to derive `sizes` when not explicitly provided.
 *   widths    — Array of responsive widths for srcSet (default: RESPONSIVE_WIDTHS)
 *   className — Additional CSS classes
 *   priority  — Boolean, preloads image for LCP
 *   loading   — 'lazy' | 'eager'
 */
export function MultiFormatImage({
    src,
    alt,
    width,
    height,
    fill = false,
    sizes,
    className,
    priority = false,
    loading,
    layout = 'full-width',
    widths,
    ...props
}) {
    // Priority images are immediately available — skip fade to avoid a late
    // opacity change that hurts Lighthouse Speed Index.
    const [loaded, setLoaded] = useState(priority);
    const imgRef = useRef(null);

    // Cached images may not fire onLoad if they're already complete before
    // React attaches the handler. Check img.complete after mount so they
    // don't get stuck invisible.
    useEffect(() => {
        if (imgRef.current?.complete) setLoaded(true);
    }, []);

    // Handle image load failures gracefully — mark as loaded so the image
    // isn't stuck invisible (opacity-0) indefinitely. This is especially
    // important for CDN URLs where OpaqueResponseBlocking or CORS issues
    // can prevent onLoad from firing.
    const handleLoad = () => setLoaded(true);
    const handleError = () => setLoaded(true);

    const isSvgFile = typeof src === 'string' && src.endsWith('.svg');

    // Paths that already have a raster extension (e.g. annotate .png diagrams)
    // are served directly without format conversion.
    const hasRasterExt =
        typeof src === 'string' && /\.(png|jpg|jpeg|webp|avif)$/i.test(src);

    // Resolve path to CDN URL; absolute URLs (already-resolved CDN URLs) pass through unchanged
    const resolvedSrc = productAsset(src);

    // Check if the resolved URL is on the Trenchmate CDN.
    // Only CDN URLs use <picture> + unoptimized — local paths go through Next.js /_next/image
    // which resizes on demand, cutting card-image payloads from ~300KB down to ~15KB.
    const isCdn = isCdnUrl(resolvedSrc);

    // Auto-derive sizes from layout preset if not explicitly provided
    const resolvedSizes = sizes || getSizesForLayout(layout);

    const loadingProp = priority ? {} : { loading: loading ?? 'lazy' };

    const visibilityClass = loaded ? 'opacity-100' : 'opacity-0';
    const composedClass = [
        className,
        visibilityClass,
        priority ? '' : 'transition-opacity duration-300',
    ]
        .filter(Boolean)
        .join(' ');

    // SVG or already-extended raster — serve directly, no format conversion needed.
    // Use unoptimized only for CDN URLs (to avoid proxying through /_next/image);
    // local paths with an explicit extension still benefit from Next.js optimization.
    // CDN URLs need crossOrigin="anonymous" to avoid Chrome's OpaqueResponseBlocking
    // (the CDN has CORS configured, but without crossorigin the browser makes no-cors
    // requests which Chrome may block).
    if (isSvgFile || hasRasterExt) {
        return (
            <Image
                ref={imgRef}
                src={resolvedSrc}
                alt={alt}
                width={width}
                height={height}
                fill={fill}
                sizes={resolvedSizes}
                className={composedClass}
                priority={priority}
                unoptimized={isCdn}
                crossOrigin={isCdn ? 'anonymous' : undefined}
                onLoad={handleLoad}
                onError={handleError}
                {...loadingProp}
                {...props}
            />
        );
    }

    // Extensionless paths (product images):
    //
    // LOCAL dev path (e.g. /product/trench-shield/trench-shield):
    //   → Use plain <Image> pointing at .webp. Next.js resizes via /_next/image,
    //     serving a ~15KB optimised WebP instead of a ~300KB original.
    //     No unoptimized, no <picture> wrapper needed — Next.js already outputs WebP.
    if (!isCdn) {
        return (
            <Image
                ref={imgRef}
                src={`${resolvedSrc}.webp`}
                alt={alt}
                width={width}
                height={height}
                fill={fill}
                sizes={resolvedSizes}
                className={composedClass}
                priority={priority}
                onLoad={handleLoad}
                onError={handleError}
                {...loadingProp}
                {...props}
            />
        );
    }

    // CDN mode — extensionless path on the CDN.
    // Build responsive srcSets using pre-generated variants on the CDN.
    // WebP source for modern browsers, JPEG/PNG fallback for legacy.

    // Resolve widths: use prop if provided, otherwise default
    const resolvedWidths =
        Array.isArray(widths) && widths.length > 0 ? widths : RESPONSIVE_WIDTHS;

    // Sort ascending and filter invalid entries
    const sortedWidths = resolvedWidths
        .filter((w) => Number.isFinite(w) && w > 0)
        .sort((a, b) => a - b);

    // Pick the middle variant as the default <Image> src so the initial
    // render serves a reasonable size (avoiding the full 1920px download).
    const middleIndex = Math.floor((sortedWidths.length - 1) / 2);
    const defaultWidth =
        sortedWidths.length > 0
            ? sortedWidths[middleIndex] || sortedWidths[0]
            : 800;

    // Fallback format detection:
    //   /logo/ or /partners/ paths → PNG (preserves transparency for brand assets)
    //   everything else → JPEG
    const isLogoOrPartner =
        typeof src === 'string' &&
        (src.includes('/logo/') || src.includes('/partners/'));
    const fallbackExt = isLogoOrPartner ? 'png' : 'jpg';

    // For priority images inside <picture>, avoid priority={true} on <Image>:
    // next/image's priority prop emits <link rel="preload" href="...jpg"> for the
    // fallback src, but the browser uses the WebP <source> instead — causing a
    // duplicate download (console: "preloaded with link preload was not used").
    // loading="eager" + fetchPriority="high" achieves the same result correctly.
    const picturePriorityProps = priority
        ? { loading: 'eager', fetchPriority: 'high' }
        : loadingProp;

    return (
        <picture className="contents">
            {/* WebP source with responsive srcSet for modern browsers */}
            <source
                srcSet={buildResponsiveSrcSet(
                    resolvedSrc,
                    'webp',
                    sortedWidths,
                )}
                type="image/webp"
                sizes={resolvedSizes}
            />

            {/* JPEG/PNG fallback with responsive srcSet for legacy browsers */}
            <Image
                ref={imgRef}
                src={`${resolvedSrc}-${defaultWidth}w.${fallbackExt}`}
                srcSet={buildResponsiveSrcSet(
                    resolvedSrc,
                    fallbackExt,
                    sortedWidths,
                )}
                alt={alt}
                width={width}
                height={height}
                fill={fill}
                sizes={resolvedSizes}
                className={composedClass}
                priority={false}
                unoptimized
                crossOrigin="anonymous"
                onLoad={handleLoad}
                onError={handleError}
                {...picturePriorityProps}
                {...props}
            />
        </picture>
    );
}
